import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, type FieldErrors } from "react-hook-form";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  BONUS_COMBINE_OPTIONS,
  CONSENT_CHANNEL_OPTIONS,
  DATA_REGION_OPTIONS,
  ENROLMENT_MODE_OPTIONS,
  EVENT_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  LANGUAGE_OPTIONS,
  MEMBER_ID_TYPE_OPTIONS,
  NEGATIVE_BALANCE_OPTIONS,
  PERSONAL_DATA_OPTIONS,
  PROGRAM_TIER_CHOICES,
  ROUNDING_OPTIONS,
  TIME_ZONE_OPTIONS,
  contactRoleOptions,
  contactsCoverLaunchRoles,
  formatCancellationPeriod,
  formatHoldDays,
  formatPointsExpiry,
  formatVersionedUrl,
  isFutureIsoDate,
  isLaunchedStatus,
  labelsFor,
  optionLabel,
  preserveAddOnly,
  samePointEconomy,
  type BonusCombineMode,
  type ConsentChannel,
  type DataRegion,
  type EnrolmentMode,
  type Industry,
  type MemberIdType,
  type NegativeBalancePolicy,
  type PerkProgram,
  type PersonalDataField,
  type PointsExpiryMode,
  type PointsRounding,
  type ProgramConfig,
  type ProgramEventType,
  type ProgramStatus,
  type ProgramTimeZone,
  type SupportedLanguage,
} from "./programs/mock-programs";
import {
  CustomAttributesField,
  EnumSelectField,
  HexColorInput,
  MultiCheckField,
  atLeastOne,
  expiryMonthsRules,
  optionalHexColor,
  optionalWholeNumber,
  parseCancellationPeriod,
  parseOptionalWholeNumber,
  parseWholeNumber,
  requiredEmail,
  requiredSlug,
  requiredText,
  requiredWholeNumber,
} from "./programs/config-controls";
import { usePerkPointScope } from "./programs/program-scope";
import {
  PROGRAM_FIELD_FROM_API,
  ProgramsApi,
  programKeys,
  programToPayload,
  type ProgramView,
} from "@/api/perkpoint/programs";
import { apiErrorMessage, apiFieldErrors, isNotFound } from "@/api/perkpoint/client";

type SetupSectionId =
  | "identity"
  | "region"
  | "economy"
  | "members"
  | "integration"
  | "team";

type EditFormValues = {
  name: string;
  programCode: string;
  programType: string;
  clientName: string;
  vatNumber: string;
  websiteUrl: string;
  domain: string;
  description: string;
  status: ProgramStatus;
  programLogoUri: string;
  billingContactName: string;
  billingContactEmail: string;
  industry: Industry;
  programOwner: string;
  brandPrimaryColor: string;
  memberTermsUrl: string;
  memberTermsVersion: string;
  privacyPolicyUrl: string;
  privacyPolicyVersion: string;
  loyaltyPointsName: string;
  costPerPoint: string;
  cashbackPercentage: string;
  pointsRounding: PointsRounding;
  pointsHoldDays: string;
  cancellationPeriodDays: string;
  pointsExpiryMode: PointsExpiryMode;
  pointsExpiryMonths: string;
  negativeBalancePolicy: NegativeBalancePolicy;
  bonusCombineMode: BonusCombineMode;
  economyEffectiveDate: string;
  economyNoticeAcknowledged: boolean;
  paymentCurrency: string;
  invoiceFrequency: string;
  offerDenomination: string;
  txnCommissionPct: string;
  timeZone: ProgramTimeZone;
  dataRegion: DataRegion;
  supportedLanguages: SupportedLanguage[];
  address: string;
  country: string;
  city: string;
  assignedTiers: string[];
  startingTier: string;
  enrolmentMode: EnrolmentMode;
  memberIdType: MemberIdType;
  personalDataCollected: PersonalDataField[];
  consentChannels: ConsentChannel[];
  dataRetentionMonths: string;
  erasurePolicy: string;
  enabledEventTypes: ProgramEventType[];
  customAttributes: string[];
  webhookUrl: string;
  messagingEnabled: boolean;
  messagingWhatsappNumber: string;
  messagingSmsSenderId: string;
  messagingFromEmail: string;
  quietHoursStart: string;
  quietHoursEnd: string;
  messageFrequencyCap: string;
  offerApprovalThreshold: string;
  adjustmentApprovalThreshold: string;
  supportAgentAdjustmentCap: string;
};

const CURRENCY_OPTIONS = [
  { value: "INR", label: "INR" },
  { value: "AED", label: "AED" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

const editFieldClass =
  "h-11 rounded-xl border-slate-200 bg-white shadow-none placeholder:text-slate-400 disabled:bg-slate-50";

const SETUP_SECTIONS: {
  id: SetupSectionId;
  label: string;
  hint: string;
  title: string;
  description: string;
}[] = [
  {
    id: "identity",
    label: "Client",
    hint: "Identity and brand",
    title: "Client and identity",
    description:
      "The client company, the program slug, and the brand members see. Terms and privacy can wait until launch.",
  },
  {
    id: "region",
    label: "Region",
    hint: "Currency and place",
    title: "Region and money",
    description:
      "Currency, time zone, and data region lock after launch. Languages can be added later, not removed.",
  },
  {
    id: "economy",
    label: "Economy",
    hint: "Points value",
    title: "Points economy",
    description:
      "Point value, rounding, hold, cancellation period, and expiry. After launch, value changes are future-dated.",
  },
  {
    id: "members",
    label: "Members",
    hint: "Enrolment and tiers",
    title: "Members",
    description:
      "How people join, which personal data you collect, and how long you keep it. Member ID type locks after launch.",
  },
  {
    id: "integration",
    label: "Integration",
    hint: "Events and messaging",
    title: "Integration",
    description:
      "Events this program accepts, where webhooks go, and how messages are sent.",
  },
  {
    id: "team",
    label: "Team",
    hint: "Contacts and controls",
    title: "Team and controls",
    description:
      "Keep this contact list. Launch needs an owner and an admin. Approval limits sit on the program, not on one person.",
  },
];

const FIELD_TAB: Record<string, SetupSectionId> = {
  name: "identity",
  programCode: "identity",
  programType: "identity",
  clientName: "identity",
  billingContactName: "identity",
  billingContactEmail: "identity",
  vatNumber: "identity",
  websiteUrl: "identity",
  domain: "identity",
  description: "identity",
  status: "identity",
  programLogoUri: "identity",
  industry: "identity",
  programOwner: "identity",
  brandPrimaryColor: "identity",
  memberTermsUrl: "identity",
  memberTermsVersion: "identity",
  privacyPolicyUrl: "identity",
  privacyPolicyVersion: "identity",
  loyaltyPointsName: "identity",
  costPerPoint: "economy",
  cashbackPercentage: "economy",
  pointsRounding: "economy",
  pointsHoldDays: "economy",
  cancellationPeriodDays: "economy",
  pointsExpiryMode: "economy",
  pointsExpiryMonths: "economy",
  negativeBalancePolicy: "economy",
  bonusCombineMode: "economy",
  economyEffectiveDate: "economy",
  economyNoticeAcknowledged: "economy",
  paymentCurrency: "region",
  invoiceFrequency: "region",
  offerDenomination: "region",
  txnCommissionPct: "region",
  timeZone: "region",
  dataRegion: "region",
  supportedLanguages: "region",
  address: "region",
  country: "identity",
  city: "region",
  assignedTiers: "members",
  startingTier: "members",
  enrolmentMode: "members",
  memberIdType: "members",
  personalDataCollected: "members",
  consentChannels: "members",
  dataRetentionMonths: "members",
  erasurePolicy: "members",
  enabledEventTypes: "integration",
  customAttributes: "integration",
  webhookUrl: "integration",
  messagingEnabled: "integration",
  messagingWhatsappNumber: "integration",
  messagingSmsSenderId: "integration",
  messagingFromEmail: "integration",
  quietHoursStart: "integration",
  quietHoursEnd: "integration",
  messageFrequencyCap: "integration",
  offerApprovalThreshold: "team",
  adjustmentApprovalThreshold: "team",
  supportAgentAdjustmentCap: "team",
};

function SectionHeader({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800/80">
        Step {String(step).padStart(2, "0")}
      </p>
      <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-slate-950">
        {title}
      </h2>
      <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
        {description}
      </p>
    </div>
  );
}

function SetupSection({
  id,
  children,
}: {
  id: SetupSectionId;
  children: ReactNode;
}) {
  const section = SETUP_SECTIONS.find((item) => item.id === id);
  const step = SETUP_SECTIONS.findIndex((item) => item.id === id) + 1;
  if (!section) return null;
  return (
    <section
      id={`program-section-${id}`}
      data-section={id}
      className="scroll-mt-28"
    >
      <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
        <CardContent className="p-6 sm:p-8">
          <SectionHeader
            step={step}
            title={section.title}
            description={section.description}
          />
          {children}
        </CardContent>
      </Card>
    </section>
  );
}

function editValuesFromProgram(program: PerkProgram): EditFormValues {
  return {
    name: program.name,
    programCode: program.programCode,
    programType: program.programType,
    clientName: program.clientName,
    vatNumber: program.vatNumber,
    websiteUrl: program.websiteUrl,
    domain: program.domain,
    description: program.description,
    status: program.status,
    programLogoUri: program.programLogoUri ?? "",
    billingContactName: program.billingContactName,
    billingContactEmail: program.billingContactEmail,
    industry: program.industry,
    programOwner: program.programOwner,
    brandPrimaryColor: program.brandPrimaryColor,
    memberTermsUrl: program.memberTermsUrl,
    memberTermsVersion: program.memberTermsVersion,
    privacyPolicyUrl: program.privacyPolicyUrl,
    privacyPolicyVersion: program.privacyPolicyVersion,
    loyaltyPointsName: program.loyaltyPointsName,
    costPerPoint: String(program.costPerPoint),
    cashbackPercentage: String(program.cashbackPercentage),
    pointsRounding: program.pointsRounding,
    pointsHoldDays:
      program.pointsHoldDays == null ? "" : String(program.pointsHoldDays),
    cancellationPeriodDays:
      program.cancellationPeriodDays == null
        ? ""
        : String(program.cancellationPeriodDays),
    pointsExpiryMode: program.pointsExpiryMode,
    pointsExpiryMonths:
      program.pointsExpiryMonths == null
        ? ""
        : String(program.pointsExpiryMonths),
    negativeBalancePolicy: program.negativeBalancePolicy,
    bonusCombineMode: program.bonusCombineMode,
    economyEffectiveDate: program.economyEffectiveDate ?? "",
    economyNoticeAcknowledged: program.economyNoticeAcknowledged,
    paymentCurrency: program.currency,
    invoiceFrequency: String(program.invoiceFrequencyInDays),
    offerDenomination: String(program.offerDenomination),
    txnCommissionPct: String(program.txnCommissionPct),
    timeZone: program.timeZone,
    dataRegion: program.dataRegion,
    supportedLanguages: [...program.supportedLanguages],
    address: program.address,
    country: program.country,
    city: program.city,
    assignedTiers: [...(program.assignedTiers ?? [])],
    startingTier: program.startingTier || "Bronze",
    enrolmentMode: program.enrolmentMode,
    memberIdType: program.memberIdType,
    personalDataCollected: [...program.personalDataCollected],
    consentChannels: [...program.consentChannels],
    dataRetentionMonths: String(program.dataRetentionMonths),
    erasurePolicy: program.erasurePolicy,
    enabledEventTypes: [...program.enabledEventTypes],
    customAttributes: [...program.customAttributes],
    webhookUrl: program.webhookUrl,
    messagingEnabled: program.messagingEnabled,
    messagingWhatsappNumber: program.messagingWhatsappNumber,
    messagingSmsSenderId: program.messagingSmsSenderId,
    messagingFromEmail: program.messagingFromEmail,
    quietHoursStart: program.quietHoursStart,
    quietHoursEnd: program.quietHoursEnd,
    messageFrequencyCap: String(program.messageFrequencyCap),
    offerApprovalThreshold: String(program.offerApprovalThreshold),
    adjustmentApprovalThreshold: String(program.adjustmentApprovalThreshold),
    supportAgentAdjustmentCap: String(program.supportAgentAdjustmentCap),
  };
}

function economyFromEdit(data: EditFormValues) {
  return {
    costPerPoint: Number(data.costPerPoint) || 0,
    pointsRounding: data.pointsRounding,
    pointsExpiryMode: data.pointsExpiryMode,
    pointsExpiryMonths:
      data.pointsExpiryMode === "never"
        ? null
        : parseWholeNumber(data.pointsExpiryMonths),
    bonusCombineMode: data.bonusCombineMode,
  };
}

function economyChangeNeedsSchedule(
  data: EditFormValues,
  program: PerkProgram,
): boolean {
  if (!isLaunchedStatus(program.status)) return false;
  return !samePointEconomy(program, economyFromEdit(data));
}

function configFromEdit(
  data: EditFormValues,
  program: PerkProgram,
): ProgramConfig {
  const locked = isLaunchedStatus(program.status);
  const scheduleChange = economyChangeNeedsSchedule(data, program);
  return {
    industry: data.industry,
    programOwner: data.programOwner.trim(),
    brandPrimaryColor: data.brandPrimaryColor.trim(),
    billingContactName: data.billingContactName.trim(),
    billingContactEmail: data.billingContactEmail.trim(),
    memberTermsUrl: data.memberTermsUrl.trim(),
    memberTermsVersion: data.memberTermsVersion.trim(),
    privacyPolicyUrl: data.privacyPolicyUrl.trim(),
    privacyPolicyVersion: data.privacyPolicyVersion.trim(),
    timeZone: locked ? program.timeZone : data.timeZone,
    dataRegion: locked ? program.dataRegion : data.dataRegion,
    supportedLanguages: preserveAddOnly(
      locked,
      program.supportedLanguages,
      data.supportedLanguages,
    ),
    pointsRounding: data.pointsRounding,
    pointsHoldDays: parseOptionalWholeNumber(data.pointsHoldDays),
    cancellationPeriodDays: parseCancellationPeriod(data.cancellationPeriodDays),
    pointsExpiryMode: data.pointsExpiryMode,
    pointsExpiryMonths:
      data.pointsExpiryMode === "never"
        ? null
        : parseWholeNumber(data.pointsExpiryMonths),
    negativeBalancePolicy: data.negativeBalancePolicy,
    bonusCombineMode: data.bonusCombineMode,
    economyEffectiveDate: scheduleChange
      ? data.economyEffectiveDate
      : program.economyEffectiveDate,
    economyNoticeAcknowledged: scheduleChange
      ? data.economyNoticeAcknowledged
      : program.economyNoticeAcknowledged,
    enrolmentMode: data.enrolmentMode,
    memberIdType: locked ? program.memberIdType : data.memberIdType,
    personalDataCollected: preserveAddOnly(
      locked,
      program.personalDataCollected,
      data.personalDataCollected,
    ),
    consentChannels: [...data.consentChannels],
    dataRetentionMonths: parseWholeNumber(data.dataRetentionMonths),
    erasurePolicy: data.erasurePolicy.trim(),
    enabledEventTypes: preserveAddOnly(
      locked,
      program.enabledEventTypes,
      data.enabledEventTypes,
    ),
    customAttributes: preserveAddOnly(
      locked,
      program.customAttributes,
      data.customAttributes,
    ),
    webhookUrl: data.webhookUrl.trim(),
    messagingEnabled: data.messagingEnabled,
    messagingWhatsappNumber: data.messagingWhatsappNumber.trim(),
    messagingSmsSenderId: data.messagingSmsSenderId.trim(),
    messagingFromEmail: data.messagingFromEmail.trim(),
    quietHoursStart: data.quietHoursStart,
    quietHoursEnd: data.quietHoursEnd,
    messageFrequencyCap: parseWholeNumber(data.messageFrequencyCap),
    offerApprovalThreshold: parseWholeNumber(data.offerApprovalThreshold),
    adjustmentApprovalThreshold: parseWholeNumber(
      data.adjustmentApprovalThreshold,
    ),
    supportAgentAdjustmentCap: parseWholeNumber(
      data.supportAgentAdjustmentCap,
    ),
  };
}

function StatusBadge({ status }: { status: ProgramStatus }) {
  const styles: Record<ProgramStatus, string> = {
    Active: "bg-green-50 text-green-700",
    Draft: "bg-slate-100 text-slate-600",
    Paused: "bg-amber-50 text-amber-700",
    Closed: "bg-red-50 text-red-700",
  };
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        styles[status],
      )}
    >
      {status}
    </span>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-900">{value || "—"}</p>
    </div>
  );
}


function ContactsBlock({
  program,
  launchGaps,
  onAdd,
  onRemove,
}: {
  program: PerkProgram;
  launchGaps: string[];
  onAdd: () => void;
  onRemove: (contactId: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-950">Contacts</h3>
          <p className="mt-1 text-sm text-slate-500">
            People responsible for this program. Launch needs an Owner and at least one Admin.
          </p>
        </div>
        <Button
          type="button"
          className="h-10 rounded-full bg-[#1B7A45] text-white hover:bg-[#16653A]"
          onClick={onAdd}
        >
          <Plus className="mr-2 h-4 w-4" /> Add contact
        </Button>
      </div>
      {program.status === "Draft" && launchGaps.length > 0 ? (
        <p className="rounded-xl bg-[#F4F8F5] px-3 py-2 text-sm text-[#145C3C]">
          Still needed before launch: {launchGaps.join(", ")}.
        </p>
      ) : null}
      {program.contacts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          No contacts yet. Add a program contact to get started.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="w-12 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {program.contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium text-slate-900">{contact.name}</TableCell>
                  <TableCell className="text-slate-600">{contact.email}</TableCell>
                  <TableCell>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {contact.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {contact.countryCode} {contact.phoneNumber}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600"
                      onClick={() => onRemove(contact.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function ProgramDetails() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const programQuery = useQuery({
    queryKey: programKeys.detail(id),
    queryFn: () => ProgramsApi.get(id),
    enabled: Boolean(id),
  });
  const program = programQuery.data;
  const [searchParams] = useSearchParams();
  // `?edit=1` comes from "Update program" in the program list
  const [editing, setEditing] = useState(
    () => searchParams.get("edit") === "1",
  );
  const { mode: scopeMode } = usePerkPointScope();
  const [activeSection, setActiveSection] = useState<SetupSectionId>("identity");
  const [contactOpen, setContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    role: "Admin",
    countryCode: "+91",
    phoneNumber: "",
  });

  const editForm = useForm<EditFormValues>({
    values: program ? editValuesFromProgram(program) : undefined,
  });
  const pointsExpiryMode = editForm.watch("pointsExpiryMode");
  const watchedCost = editForm.watch("costPerPoint");
  const watchedRounding = editForm.watch("pointsRounding");
  const watchedExpiryMonths = editForm.watch("pointsExpiryMonths");
  const watchedBonus = editForm.watch("bonusCombineMode");
  const watchedTiers = editForm.watch("assignedTiers") ?? [];
  const watchedStartingTier = editForm.watch("startingTier");

  const refresh = (next?: ProgramView) => {
    if (next) queryClient.setQueryData(programKeys.detail(id), next);
    queryClient.invalidateQueries({ queryKey: programKeys.all });
  };
  useEffect(() => {
    const nodes = SETUP_SECTIONS.map((section) =>
      document.getElementById(`program-section-${section.id}`),
    ).filter((node): node is HTMLElement => node !== null);
    if (nodes.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const id = visible?.target.getAttribute("data-section") as
          | SetupSectionId
          | null;
        if (id) setActiveSection(id);
      },
      { rootMargin: "-18% 0px -62% 0px", threshold: 0.05 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [editing, program?.id]);

  const saveMutation = useMutation({
    mutationFn: ({ next, tiersChanged }: { next: PerkProgram; tiersChanged: boolean }) =>
      ProgramsApi.update(id, programToPayload(next, { includeTiers: tiersChanged })),
    onSuccess: (updated) => {
      refresh(updated);
      setEditing(false);
      toast.success("Program updated");
    },
    onError: (error) => {
      let firstField: string | undefined;
      Object.entries(apiFieldErrors(error)).forEach(([apiField, message]) => {
        const mapped = PROGRAM_FIELD_FROM_API[apiField];
        const field = (mapped === "code" ? "programCode" : mapped) as keyof EditFormValues | undefined;
        if (!field) return;
        editForm.setError(field, { message });
        firstField ??= field;
      });
      const section = firstField ? FIELD_TAB[firstField] : undefined;
      if (section) {
        setActiveSection(section as SetupSectionId);
        document
          .getElementById(`program-section-${section}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      toast.error(apiErrorMessage(error, "Could not save the program"));
    },
  });

  if (!program) {
    return (
      <div className="space-y-4 pb-2">
        <p className="text-sm text-muted-foreground">
          {programQuery.isLoading
            ? "Loading program…"
            : programQuery.isError && !isNotFound(programQuery.error)
              ? apiErrorMessage(programQuery.error, "Could not load the program")
              : "Program not found."}
        </p>
        <Button asChild variant="outline" className="bg-white">
          <Link to="/perkpoint/program/setup">Back to Program Setup</Link>
          </Button>
      </div>
    );
  }

  const scrollToSection = (id: SetupSectionId) => {
    setActiveSection(id);
    document.getElementById(`program-section-${id}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openEdit = () => {
    setEditing(true);
    setActiveSection("identity");
    window.setTimeout(() => scrollToSection("identity"), 40);
  };

  const cancelEdit = () => {
    editForm.reset(editValuesFromProgram(program));
    setEditing(false);
  };

  const saveEdit = (data: EditFormValues) => {
    const configLocked = isLaunchedStatus(program.status);
    if (isLaunchedStatus(data.status)) {
      if (!data.memberTermsUrl.trim() || !data.privacyPolicyUrl.trim()) {
        toast.error(
          "Member terms and a privacy policy URL are required before launch",
        );
        scrollToSection("identity");
        return;
      }
      if (
        program.status === "Draft" &&
        !contactsCoverLaunchRoles(program.contacts)
      ) {
        toast.error(
          "Launch needs an Owner and at least one Admin on the contact list. This draft can still be saved.",
        );
        return;
      }
    }
    if (economyChangeNeedsSchedule(data, program)) {
      if (!isFutureIsoDate(data.economyEffectiveDate)) {
        toast.error(
          "Point value, rounding, expiry, and bonus changes need a future effective date",
        );
        scrollToSection("economy");
        return;
      }
      if (!data.economyNoticeAcknowledged) {
        toast.error("Acknowledge the member notice for this points change");
        scrollToSection("economy");
        return;
      }
    }
    if (data.assignedTiers.length === 0) {
      toast.error("Keep at least one tier assigned");
      scrollToSection("members");
      return;
    }
    const startingTier = data.assignedTiers.includes(data.startingTier)
      ? data.startingTier
      : data.assignedTiers[0];
    const next: PerkProgram = {
      ...program,
      ...configFromEdit(data, program),
      name: data.name.trim(),
      programCode: configLocked ? program.programCode : data.programCode.trim(),
      programType: data.programType,
      clientName: data.clientName.trim(),
      vatNumber: data.vatNumber.trim(),
      websiteUrl: data.websiteUrl.trim(),
      domain: data.domain.trim(),
      description: data.description.trim(),
      status: data.status,
      programLogoUri: data.programLogoUri.trim() || null,
      loyaltyPointsName: data.loyaltyPointsName.trim(),
      costPerPoint: Number(data.costPerPoint) || 0,
      cashbackPercentage: Number(data.cashbackPercentage) || 0,
      currency: configLocked ? program.currency : data.paymentCurrency,
      invoiceFrequencyInDays: Number(data.invoiceFrequency) || 30,
      offerDenomination: Number(data.offerDenomination) || 1,
      txnCommissionPct: Number(data.txnCommissionPct) || 0,
      address: data.address.trim(),
      country: data.country.trim(),
      city: data.city.trim(),
      assignedTiers: [...data.assignedTiers],
      startingTier,
    };
    const tiersChanged =
      next.assignedTiers.join("|") !== program.assignedTiers.join("|") ||
      next.startingTier !== program.startingTier;
    saveMutation.mutate({ next, tiersChanged });
  };

  const onInvalidEdit = (errors: FieldErrors<EditFormValues>) => {
    const section = SETUP_SECTIONS.find((item) =>
      Object.keys(errors).some((key) => FIELD_TAB[key] === item.id),
    );
    if (section) scrollToSection(section.id);
  };

  const handleAddContact = () => {
    if (!newContact.name.trim() || !newContact.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!newContact.role.trim()) {
      toast.error("Choose a contact role");
      return;
    }
    ProgramsApi.addContact(program.id, {
      name: newContact.name.trim(),
      email: newContact.email.trim(),
      role: newContact.role.trim(),
      countryCode: newContact.countryCode,
      phoneNumber: newContact.phoneNumber.trim(),
    })
      .then(() => {
        refresh();
        setNewContact({
          name: "",
          email: "",
          role: "Admin",
          countryCode: "+91",
          phoneNumber: "",
        });
        setContactOpen(false);
        toast.success("Contact added");
      })
      .catch((error) => toast.error(apiErrorMessage(error, "Could not add the contact")));
  };

  const handleRemoveContact = (contactId: string) => {
    ProgramsApi.removeContact(program.id, contactId)
      .then(() => {
        refresh();
        toast.success("Contact removed");
      })
      .catch((error) => toast.error(apiErrorMessage(error, "Could not remove the contact")));
  };

  const launched = isLaunchedStatus(program.status);
  const scheduleRequired =
    launched &&
    watchedRounding != null &&
    !samePointEconomy(program, {
      costPerPoint: Number(watchedCost) || 0,
      pointsRounding: watchedRounding,
      pointsExpiryMode,
      pointsExpiryMonths:
        pointsExpiryMode === "never" ? null : Number(watchedExpiryMonths) || null,
      bonusCombineMode: watchedBonus,
    });
  const toggleEditTier = (tierName: string) => {
    const current = editForm.getValues("assignedTiers");
    if (current.includes(tierName)) {
      if (current.length === 1) {
        toast.error("Keep at least one tier assigned");
        return;
      }
      const next = current.filter((tier) => tier !== tierName);
      editForm.setValue("assignedTiers", next);
      if (editForm.getValues("startingTier") === tierName) {
        editForm.setValue("startingTier", next[0]);
      }
      return;
    }
    editForm.setValue("assignedTiers", [...current, tierName]);
  };
  const launchGaps = program.launchGaps;

  return (
    <div className="mx-auto max-w-6xl overflow-x-hidden pb-10">
      <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          {scopeMode !== "manager" ? (
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="-ml-2 mb-3 h-8 rounded-full px-2 text-slate-500 hover:text-slate-900"
            >
              <Link to="/perkpoint/program/setup">
                <ArrowLeft className="h-4 w-4" />
                Programs
              </Link>
            </Button>
          ) : null}
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-800/80">
            Perk Point
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h1 className="text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
              {editing ? "Update program" : program.name}
            </h1>
            <StatusBadge status={program.status} />
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500 sm:text-base">
            {editing
              ? "Same six steps as program creation. Save when the setup looks right."
              : "Client, region, economy, members, integration, and team — the same setup used to create this program."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {editing ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-full border-slate-200 bg-white px-5"
                onClick={cancelEdit}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-program-form"
                className="h-11 rounded-full bg-[#1B7A45] px-5 text-white shadow-none hover:bg-[#16653A]"
              >
                Save changes
              </Button>
            </>
          ) : (
            <Button
              type="button"
              className="h-11 rounded-full bg-[#1B7A45] px-5 text-white shadow-none hover:bg-[#16653A]"
              onClick={openEdit}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit program
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className="sticky top-0 hidden lg:block" aria-label="Program sections">
          <ol className="space-y-1 border-l border-slate-200">
            {SETUP_SECTIONS.map((section, index) => {
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "relative flex w-full items-start gap-3 rounded-r-xl px-4 py-2.5 text-left transition-colors",
                      isActive
                        ? "bg-emerald-50/80 text-emerald-950"
                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-900",
                    )}
                  >
                    <span
                      className={cn(
                        "absolute -left-px top-3 h-5 w-0.5 rounded-full",
                        isActive ? "bg-[#1B7A45]" : "bg-transparent",
                      )}
                      aria-hidden="true"
                    />
                    <span className="w-5 shrink-0 text-xs font-semibold tabular-nums">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">
                        {section.label}
                      </span>
                      <span className="mt-0.5 block text-xs font-normal text-slate-400">
                        {section.hint}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="min-w-0">
          <div className="mb-5 lg:hidden">
            <div className="flex flex-wrap gap-2">
              {SETUP_SECTIONS.map((section, index) => {
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-sm font-medium",
                      isActive
                        ? "bg-[#145C3C] text-white"
                        : "bg-white text-slate-600 ring-1 ring-slate-200",
                    )}
                  >
                    {index + 1}. {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!editing ? (
            <div className="space-y-5">
              <SetupSection id="identity">
                <div className="grid gap-4 md:grid-cols-2">
                  <ReadField label="Legal name" value={program.clientName} />
                  <ReadField label="Country" value={program.country} />
                  <ReadField label="Billing contact" value={program.billingContactName} />
                  <ReadField label="Billing email" value={program.billingContactEmail} />
                  <div className="md:col-span-2">
                    <ReadField label="Program name" value={program.name} />
                  </div>
                  <ReadField label="Program code / slug" value={program.programCode} />
                  <ReadField label="Industry" value={optionLabel(INDUSTRY_OPTIONS, program.industry)} />
                  <ReadField label="Program owner" value={program.programOwner} />
                  <ReadField label="VAT / Tax ID" value={program.vatNumber} />
                  <div className="md:col-span-2">
                    <ReadField label="Website URL" value={program.websiteUrl} />
                  </div>
                  <ReadField label="Domain" value={program.domain} />
                  <ReadField label="Program type" value={program.programType} />
                  <div className="md:col-span-2">
                    <ReadField label="Logo URL" value={program.programLogoUri ?? ""} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Brand colour</p>
                    <div className="mt-1 flex items-center gap-2">
                      {program.brandPrimaryColor ? (
                        <span
                          className="h-4 w-4 rounded-full border border-slate-200"
                          style={{ backgroundColor: program.brandPrimaryColor }}
                        />
                      ) : null}
                      <p className="text-sm font-medium text-slate-900">
                        {program.brandPrimaryColor || "—"}
                      </p>
                    </div>
                  </div>
                  <ReadField label="Points name" value={program.loyaltyPointsName} />
                  <ReadField label="Member terms URL" value={program.memberTermsUrl} />
                  <ReadField label="Terms version" value={program.memberTermsVersion} />
                  <ReadField label="Privacy policy URL" value={program.privacyPolicyUrl} />
                  <ReadField label="Privacy version" value={program.privacyPolicyVersion} />
                  <div className="md:col-span-2">
                    <ReadField label="Description" value={program.description} />
                  </div>
                </div>
              </SetupSection>

              <SetupSection id="region">
                <div className="grid gap-4 md:grid-cols-2">
                  <ReadField label="Currency" value={program.currency} />
                  <ReadField label="Time zone" value={program.timeZone} />
                  <ReadField label="Data region" value={program.dataRegion} />
                  <ReadField
                    label="Supported languages"
                    value={labelsFor(LANGUAGE_OPTIONS, program.supportedLanguages)}
                  />
                  <ReadField label="Invoice frequency" value={`${program.invoiceFrequencyInDays} days`} />
                  <ReadField label="Offer denomination" value={String(program.offerDenomination)} />
                  <ReadField label="Txn commission %" value={`${program.txnCommissionPct}%`} />
                  <ReadField label="City" value={program.city} />
                  <div className="md:col-span-2">
                    <ReadField label="Address" value={program.address} />
                  </div>
                </div>
              </SetupSection>

              <SetupSection id="economy">
                <div className="grid gap-4 md:grid-cols-2">
                  <ReadField label="Cost per point" value={String(program.costPerPoint)} />
                  <ReadField label="Cashback %" value={`${program.cashbackPercentage}%`} />
                  <ReadField label="Rounding" value={optionLabel(ROUNDING_OPTIONS, program.pointsRounding)} />
                  <p className="md:col-span-2 rounded-xl bg-[#F7F4EE] px-4 py-3 text-sm leading-relaxed text-slate-600">
                    Booking points stay pending until the day after checkout, then move into the available balance on their own. A hold or a cancellation period, when set, can only make that later. Leaving both blank does not release points before checkout.
                  </p>
                  <ReadField label="Hold before spendable" value={formatHoldDays(program.pointsHoldDays)} />
                  <ReadField
                    label="Cancellation period"
                    value={formatCancellationPeriod(program.cancellationPeriodDays)}
                  />
                  <ReadField
                    label="Points expiry"
                    value={formatPointsExpiry(program.pointsExpiryMode, program.pointsExpiryMonths)}
                  />
                  <ReadField
                    label="Negative balance"
                    value={optionLabel(NEGATIVE_BALANCE_OPTIONS, program.negativeBalancePolicy)}
                  />
                  <ReadField
                    label="How bonuses combine"
                    value={optionLabel(BONUS_COMBINE_OPTIONS, program.bonusCombineMode)}
                  />
                  <ReadField label="Economy effective date" value={program.economyEffectiveDate ?? ""} />
                  <ReadField
                    label="Member notice"
                    value={program.economyNoticeAcknowledged ? "Acknowledged" : ""}
                  />
                  <ReadField label="Points issued" value={program.pointsIssued} />
                </div>
              </SetupSection>

              <SetupSection id="members">
                <div className="grid gap-4 md:grid-cols-2">
                  <ReadField label="Enrolment mode" value={optionLabel(ENROLMENT_MODE_OPTIONS, program.enrolmentMode)} />
                  <ReadField label="Member ID type" value={optionLabel(MEMBER_ID_TYPE_OPTIONS, program.memberIdType)} />
                  <ReadField
                    label="Personal data collected"
                    value={labelsFor(PERSONAL_DATA_OPTIONS, program.personalDataCollected)}
                  />
                  <ReadField
                    label="Consent capture"
                    value={labelsFor(CONSENT_CHANNEL_OPTIONS, program.consentChannels)}
                  />
                  <ReadField label="Keep data for" value={`${program.dataRetentionMonths} months`} />
                  <ReadField label="Erasure policy" value={program.erasurePolicy} />
                  <ReadField label="Starting tier" value={program.startingTier || "Bronze"} />
                  <div>
                    <p className="text-xs text-muted-foreground">Assigned tiers</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(program.assignedTiers?.length
                        ? program.assignedTiers
                        : ["Bronze", "Silver", "Gold", "Platinum"]
                      ).map((tier) => (
                        <span
                          key={tier}
                          className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"
                        >
                          {tier}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </SetupSection>

              <SetupSection id="integration">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <ReadField
                      label="Event types"
                      value={labelsFor(EVENT_TYPE_OPTIONS, program.enabledEventTypes)}
                    />
                  </div>
                  <ReadField label="Custom attributes" value={program.customAttributes.join(", ")} />
                  <ReadField label="Messaging used" value={program.messagingEnabled ? "Yes" : "No"} />
                  <div className="md:col-span-2">
                    <ReadField label="Webhook URL" value={program.webhookUrl} />
                  </div>
                </div>
              </SetupSection>

              <SetupSection id="team">
                <ContactsBlock
                  program={program}
                  launchGaps={launchGaps}
                  onAdd={() => setContactOpen(true)}
                  onRemove={handleRemoveContact}
                />
                <div className="mt-6 grid gap-4 border-t border-slate-100 pt-6 md:grid-cols-2">
                  <ReadField label="Offers above (points)" value={`${program.offerApprovalThreshold} points`} />
                  <ReadField label="Adjustments above (points)" value={`${program.adjustmentApprovalThreshold} points`} />
                  <ReadField label="Support agent point-adjustment cap" value={`${program.supportAgentAdjustmentCap} points`} />
                </div>
              </SetupSection>
            </div>
          ) : (
            <Form {...editForm}>
            <form
              id="edit-program-form"
              onSubmit={editForm.handleSubmit(saveEdit, onInvalidEdit)}
              className="space-y-5"
            >
              <SetupSection id="identity">
                <div className="space-y-4">
                  <FormField
                    control={editForm.control}
                    name="name"
                    rules={{ required: "Program name is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Program name*</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Changeable after launch.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="programCode"
                    rules={requiredSlug("Program code")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Program code / slug*</FormLabel>
                        <FormControl>
                          <Input
                            className={editFieldClass}
                            disabled={launched}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {launched
                            ? "Locked after launch. This code is used in API paths."
                            : "Used in API paths. Locked once the program is Active or Paused."}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={editForm.control}
                      name="programType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Program type</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger className={editFieldClass}>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="EARN">Earn</SelectItem>
                              <SelectItem value="BURN">Burn</SelectItem>
                              <SelectItem value="BOTH">Earn & Burn</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger className={editFieldClass}>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Draft">Draft</SelectItem>
                              <SelectItem value="Paused">Paused</SelectItem>
                              <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription className="text-xs">
                            Leaving draft requires terms, privacy, an Owner, and an Admin.
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={editForm.control}
                    name="clientName"
                    rules={requiredText("Legal name")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Legal name*</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={editForm.control}
                      name="country"
                      rules={requiredText("Country")}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country*</FormLabel>
                          <FormControl>
                            <Input className={editFieldClass} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="billingContactName"
                      rules={requiredText("Billing contact")}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Billing contact*</FormLabel>
                          <FormControl>
                            <Input className={editFieldClass} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="billingContactEmail"
                      rules={requiredEmail("Billing email")}
                      render={({ field }) => (
                        <FormItem className="sm:col-span-2">
                          <FormLabel>Billing email*</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              className={editFieldClass}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={editForm.control}
                    name="vatNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT / Tax ID</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="websiteUrl"
                    rules={{ required: "Website URL is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website URL*</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="domain"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Domain</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea className="bg-white" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="industry"
                    label="Industry*"
                    options={INDUSTRY_OPTIONS}
                    triggerClassName={editFieldClass}
                    rules={{ required: "Industry is required" }}
                  />
                  <FormField
                    control={editForm.control}
                    name="programOwner"
                    rules={requiredText("Program owner")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {launched ? "Transfer program owner*" : "Program owner*"}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className={editFieldClass}
                            placeholder="Admin user"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {launched
                            ? "After launch the owner can be reassigned to another admin. Clearing the owner is not allowed."
                            : "An admin user. After launch this becomes a transfer and cannot be blanked."}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="programLogoUri"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Logo URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https:// optional"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="brandPrimaryColor"
                    rules={optionalHexColor()}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Brand colour</FormLabel>
                        <FormControl>
                          <HexColorInput
                            name={field.name}
                            inputRef={field.ref}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            className={editFieldClass}
                            pickerClassName="h-10"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="loyaltyPointsName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Points name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Westay Points"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Optional for a draft. This is the loyalty points name.
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="memberTermsUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Member terms URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-amber-800/90">
                          Required before launch, not to save a draft
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="memberTermsVersion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Terms version</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 1 or Oct 2026 notice"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Stored with the URL. Not a legal archive.
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="privacyPolicyUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Privacy policy URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-amber-800/90">
                          Required before launch, not to save a draft
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="privacyPolicyVersion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Privacy version</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 1 or Oct 2026 notice"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </SetupSection>

              <SetupSection id="region">
                <div className="space-y-4">
                  {launched ? (
                    <p className="rounded-xl bg-[#F7F4EE] px-3 py-2 text-xs leading-relaxed text-[#10261c]">
                      Currency, time zone, and data region stay fixed while the
                      program is Active or Paused. Languages already saved stay
                      on; you can add another.
                    </p>
                  ) : null}
                  <EnumSelectField
                    control={editForm.control}
                    name="paymentCurrency"
                    label="Currency*"
                    options={CURRENCY_OPTIONS}
                    triggerClassName={editFieldClass}
                    disabled={launched}
                    rules={{ required: "Currency is required" }}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="timeZone"
                    label="Time zone*"
                    options={TIME_ZONE_OPTIONS}
                    triggerClassName={editFieldClass}
                    disabled={launched}
                    rules={{ required: "Time zone is required" }}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="dataRegion"
                    label="Data region*"
                    options={DATA_REGION_OPTIONS}
                    triggerClassName={editFieldClass}
                    disabled={launched}
                    rules={{ required: "Data region is required" }}
                  />
                  <MultiCheckField
                    control={editForm.control}
                    name="supportedLanguages"
                    label="Supported languages*"
                    options={LANGUAGE_OPTIONS}
                    lockedValues={launched ? program.supportedLanguages : []}
                    rules={atLeastOne("Select at least one language")}
                    hint="At least one. After launch, add only."
                  />
                  <FormField
                    control={editForm.control}
                    name="invoiceFrequency"
                    rules={requiredWholeNumber("Invoice frequency", 1)}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice frequency (days)*</FormLabel>
                        <FormControl>
                          <Input type="number" className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="offerDenomination"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Offer denomination</FormLabel>
                        <FormControl>
                          <Input type="number" className={editFieldClass} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="txnCommissionPct"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Txn commission %</FormLabel>
                        <FormControl>
                          <Input type="number" className={editFieldClass} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="address"
                    rules={requiredText("Address")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address*</FormLabel>
                        <FormControl>
                          <Textarea className="bg-white" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="city"
                    rules={requiredText("City")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City*</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SetupSection>

              <SetupSection id="economy">
                <div className="space-y-4">
                  <p className="rounded-xl bg-[#F7F4EE] px-3 py-2 text-xs leading-relaxed text-[#10261c]">
                    Expiry never applies backwards to points members already
                    hold. After launch, point value, rounding, expiry, and how
                    bonuses combine change only on a future date. That change
                    needs approval and a member notice.
                  </p>
                  <FormField
                    control={editForm.control}
                    name="costPerPoint"
                    rules={{ required: "Point value is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Point value (cost per point)*</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="cashbackPercentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cashback %</FormLabel>
                        <FormControl>
                          <Input type="number" className={editFieldClass} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="pointsRounding"
                    label="Rounding*"
                    options={ROUNDING_OPTIONS}
                    triggerClassName={editFieldClass}
                    rules={{ required: "Rounding is required" }}
                  />
                  <FormField
                    control={editForm.control}
                    name="pointsHoldDays"
                    rules={optionalWholeNumber("Hold days")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default hold before points are spendable (days)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            placeholder="Optional"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Optional extra days after the booking. Leave blank to follow checkout only. Booking points still wait until the day after checkout.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="cancellationPeriodDays"
                    rules={optionalWholeNumber("Cancellation period")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cancellation period (days)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            placeholder="Optional"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Optional. A cancellation inside this window takes the points back. After the window, the cancellation is refused. Leave blank for no extra window. Points still wait until the day after checkout.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="pointsExpiryMode"
                    label="Default points expiry*"
                    options={[
                      { value: "months", label: "After a number of months" },
                      { value: "never", label: "Never" },
                    ]}
                    triggerClassName={editFieldClass}
                    rules={{ required: "Choose a points expiry" }}
                    hint="Never applies backwards."
                  />
                  <FormField
                    control={editForm.control}
                    name="pointsExpiryMonths"
                    rules={expiryMonthsRules(() =>
                      editForm.getValues("pointsExpiryMode"),
                    )}
                    render={({ field }) => (
                      <FormItem className={pointsExpiryMode === "months" ? "" : "hidden"}>
                        <FormLabel>Expiry months*</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="negativeBalancePolicy"
                    label="Negative balance*"
                    options={NEGATIVE_BALANCE_OPTIONS}
                    triggerClassName={editFieldClass}
                    rules={{ required: "Choose a negative balance policy" }}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="bonusCombineMode"
                    label="How bonuses combine*"
                    options={BONUS_COMBINE_OPTIONS}
                    triggerClassName={editFieldClass}
                    rules={{ required: "Choose how bonuses combine" }}
                  />
                  {launched ? (
                    <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
                      <p className="text-sm text-amber-950">
                        {scheduleRequired
                          ? "This economy change is future-dated. Pick the effective date and acknowledge the member notice."
                          : "If you change point value, rounding, expiry, or bonus combination, an effective date and member notice are required."}
                      </p>
                      <FormField
                        control={editForm.control}
                        name="economyEffectiveDate"
                        rules={{
                          validate: (value: string) =>
                            !economyChangeNeedsSchedule(
                              editForm.getValues(),
                              program,
                            ) ||
                            isFutureIsoDate(value) ||
                            "Choose a future effective date",
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Effective date</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                className={editFieldClass}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editForm.control}
                        name="economyNoticeAcknowledged"
                        rules={{
                          validate: (value: boolean) =>
                            !economyChangeNeedsSchedule(
                              editForm.getValues(),
                              program,
                            ) ||
                            value === true ||
                            "Acknowledge the member notice",
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <label className="flex items-start gap-3 text-sm text-slate-800">
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={(next) =>
                                  field.onChange(next === true)
                                }
                              />
                              <span>
                                I acknowledge this change needs approval and
                                members will be notified before it takes effect.
                              </span>
                            </label>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ) : null}
                </div>
              </SetupSection>

              <SetupSection id="members">
                <div className="space-y-4">
                  <EnumSelectField
                    control={editForm.control}
                    name="enrolmentMode"
                    label="Enrolment mode*"
                    options={ENROLMENT_MODE_OPTIONS}
                    triggerClassName={editFieldClass}
                    rules={{ required: "Enrolment mode is required" }}
                  />
                  <EnumSelectField
                    control={editForm.control}
                    name="memberIdType"
                    label="Member ID type*"
                    options={MEMBER_ID_TYPE_OPTIONS}
                    triggerClassName={editFieldClass}
                    disabled={launched}
                    rules={{ required: "Member ID type is required" }}
                    hint={
                      launched
                        ? "Locked after launch."
                        : "Locked once the program is Active or Paused."
                    }
                  />
                  <MultiCheckField
                    control={editForm.control}
                    name="personalDataCollected"
                    label="Personal data collected*"
                    options={PERSONAL_DATA_OPTIONS}
                    lockedValues={launched ? program.personalDataCollected : []}
                    rules={atLeastOne("Select at least one personal data field")}
                    hint="At least one. After launch, add only."
                  />
                  <MultiCheckField
                    control={editForm.control}
                    name="consentChannels"
                    label="Consent capture*"
                    options={CONSENT_CHANNEL_OPTIONS}
                    rules={atLeastOne("Select at least one consent channel")}
                  />
                  <FormField
                    control={editForm.control}
                    name="dataRetentionMonths"
                    rules={requiredWholeNumber("Data retention months", 1)}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Keep data for (months)*</FormLabel>
                        <FormControl>
                          <Input type="number" min={1} className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="erasurePolicy"
                    rules={requiredText("Erasure policy")}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Erasure policy*</FormLabel>
                        <FormControl>
                          <Input className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="space-y-3 border-t border-slate-100 pt-4">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Assigned tiers</p>
                      <p className="text-xs text-slate-500">
                        Starting tier: {watchedStartingTier || "Bronze"}
                      </p>
                    </div>
                    <FormField
                      control={editForm.control}
                      name="startingTier"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Starting tier</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className={editFieldClass}>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {watchedTiers.map((tier) => (
                                <SelectItem key={tier} value={tier}>
                                  {tier}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <div className="flex flex-wrap gap-2">
                      {PROGRAM_TIER_CHOICES.map((tier) => {
                        const selected = watchedTiers.includes(tier.name);
                        return (
                          <button
                            key={tier.name}
                            type="button"
                            onClick={() => toggleEditTier(tier.name)}
                            className={cn(
                              "rounded-full border px-3 py-1.5 text-sm",
                              selected
                                ? "border-[#1B7A45] bg-emerald-50 text-slate-900"
                                : "border-slate-200 bg-white text-slate-600",
                            )}
                          >
                            {tier.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </SetupSection>

              <SetupSection id="integration">
                <div className="space-y-4">
                  <MultiCheckField
                    control={editForm.control}
                    name="enabledEventTypes"
                    label="Event types*"
                    options={EVENT_TYPE_OPTIONS}
                    lockedValues={launched ? program.enabledEventTypes : []}
                    rules={atLeastOne("Enable at least one event type")}
                    hint="At least one. After launch, add only."
                  />
                  <CustomAttributesField
                    control={editForm.control}
                    name="customAttributes"
                    lockedValues={launched ? program.customAttributes : []}
                    inputClassName={editFieldClass}
                  />
                  <FormField
                    control={editForm.control}
                    name="webhookUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Webhook URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https:// optional"
                            className={editFieldClass}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="messagingEnabled"
                    render={({ field }) => (
                      <FormItem>
                        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-[#F7F4EE] px-3 py-3 text-sm">
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(next) => field.onChange(next === true)}
                          />
                          <span>
                            Campaigns and messaging are used. Sender, quiet
                            hours, and frequency are set on each campaign.
                          </span>
                        </label>
                      </FormItem>
                    )}
                  />
                </div>
              </SetupSection>

              <SetupSection id="team">
                <ContactsBlock
                  program={program}
                  launchGaps={launchGaps}
                  onAdd={() => setContactOpen(true)}
                  onRemove={handleRemoveContact}
                />
                <div className="mt-6 grid gap-4 border-t border-slate-100 pt-6 md:grid-cols-2">
                  <p className="text-xs leading-relaxed text-slate-500 md:col-span-2">
                    Approval limits sit on the program, not on one person.
                  </p>
                  <FormField
                    control={editForm.control}
                    name="offerApprovalThreshold"
                    rules={requiredWholeNumber("Offer approval threshold", 0)}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Offers above (points)*</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="adjustmentApprovalThreshold"
                    rules={requiredWholeNumber("Adjustment approval threshold", 0)}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Adjustments above (points)*</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="supportAgentAdjustmentCap"
                    rules={requiredWholeNumber("Support agent adjustment cap", 0)}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Support agent point-adjustment cap*</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} className={editFieldClass} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SetupSection>
              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-white"
                  onClick={cancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  Save changes
                </Button>
              </div>
            </form>
          </Form>
          )}
        </div>
      </div>

      {/* Add contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add contact</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Name*</Label>
              <Input
                className="h-10 bg-white"
                value={newContact.name}
                onChange={(e) =>
                  setNewContact((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Email*</Label>
              <Input
                className="h-10 bg-white"
                value={newContact.email}
                onChange={(e) =>
                  setNewContact((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select
                value={newContact.role}
                onValueChange={(role) =>
                  setNewContact((prev) => ({ ...prev, role }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {contactRoleOptions(newContact.role).map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                className="h-10 bg-white"
                value={newContact.phoneNumber}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    phoneNumber: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="bg-white"
              onClick={() => setContactOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={handleAddContact}
            >
              Add contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
